<template>
  <InputGroup
    :bs="wrapperBs"
    :data-active="isOpen || null"
    :disabled="disabled"
    :readonly="readonly"
    v-bind="$attrs"
  >
    <template #left>
      <slot name="left" />
    </template>
    <template #custom>
      <BeerInputTag
        v-for="item of items"
        :key="item.id"
        :tag-props="{
          textBs: {
            maxWidth: 160,
          },
        }"
        :disabled="disabled"
        :readonly="readonly"
        @click:icon="() => removeItem(item)"
      >
        {{ item.text }}
      </BeerInputTag>
      <NewBsBox
        v-if="!(disabled || readonly)"
        :bs="{ 'cmp.inputGroup': 'flexInput' }"
        :placeholder="selectPlaceholder"
        :readonly="!filterable || null"
        :value="filterable ? filterText : null"
        as="input"
        type="text"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
      />
      <slot v-if="isOpen" />
    </template>
    <template
      v-if="!readonly"
      #right
    >
      <slot name="right">
        <BeerIcon
          :icon="icon"
          :size="14"
          :bs="{ mr: 12 }"
          color="mute"
        />
      </slot>
    </template>
  </InputGroup>
</template>

<script lang="ts">
  import {
    computed, defineComponent, ref, PropType,
  } from 'vue'
  import BeerInputTag from '@xhs/yam-beer/cmp/common/Tag/InputTag.vue'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { InputMode, ExtendItem } from '../../../compose/options/useOptions'
  import InputGroup from '../InputGroup/InputGroup.vue'

  export default defineComponent({
    name: 'SelectTagButton',
    components: {
      InputGroup,
      BeerIcon,
      BeerInputTag,
      NewBsBox,
    },
    inheritAttrs: false,
    props: {
      bs: {
        type: Object,
        default: () => ({}),
      },
      inputMode: {
        type: String as PropType<InputMode>,
        default: 'select',
      },
      filterText: {
        type: String,
      },
      items: {
        type: Array as PropType<ExtendItem[]>,
        required: true,
      },
      isOpen: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        required: false,
      },
      removeItem: {
        type: Function,
        required: true,
      },
    },
    emits: ['input'],
    setup(props, { emit }) {
      const focused = ref(false)
      const filterable = computed(
        () => {
          if (props.inputMode === 'select') {
            return false
          }
          return focused.value || props.isOpen
        },
      )
      const selectPlaceholder = computed(
        () => {
          if (props.placeholder) {
            return props.placeholder
          }
          if (filterable.value) {
            if (props.inputMode === 'filter') {
              return '筛选...'
            }
            if (props.inputMode === 'search') {
              return '搜索...'
            }
          }
          if (props.items.length < 1) {
            return '请选择选项'
          }
          return null
        },
      )
      const icon = computed(
        () => {
          if (filterable.value) {
            if (props.inputMode === 'filter') {
              return 'filtrate'
            }
            if (props.inputMode === 'search') {
              return 'search'
            }
          }
          return 'arrow-down-m'
        },
      )
      return {
        wrapperBs: computed(() => {
          let cursor: string|undefined = 'pointer'
          if (props.disabled) {
            cursor = 'not-allowed'
          }
          if (props.readonly) {
            cursor = undefined
          }
          return {
            cursor,
            input: {
              cursor: 'unset',
            },
            ...props.bs,
          }
        }),
        selectPlaceholder,
        filterable,
        icon,
        handleFocus() {
          focused.value = true
        },
        handleBlur() {
          focused.value = false
        },
        handleInput(e: Event) {
          emit('input', e)
        },
      }
    },
  })
</script>
